/* Compact "log a call" modal used by Pipeline cards.
 *
 * Flow: user taps the phone icon on a Pipeline card → we open the native
 * `tel:` link (their phone's dialer) in a new tab AND pop this modal. The
 * second they're off the call, the modal is already up — they pick an
 * outcome chip, optionally jot notes/duration, hit Save. POSTs to
 * /api/leads/{lead_id}/call-logs (same endpoint the Lead page uses).
 *
 * Intentionally minimal — meant to be fillable in under 10 seconds so
 * power-dialing through a Kanban column doesn't slow down.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PhoneCall, PhoneOff, Voicemail, Phone, AlertCircle, MessageCircle,
  Loader2, X,
} from "lucide-react";

const OUTCOMES = [
  { key: "talked",       label: "Talked",       Icon: PhoneCall,    tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  { key: "voicemail",    label: "Voicemail",    Icon: Voicemail,    tone: "text-sky-700 bg-sky-50 border-sky-200" },
  { key: "no_answer",    label: "No answer",    Icon: PhoneOff,     tone: "text-slate-700 bg-slate-50 border-slate-200" },
  { key: "busy",         label: "Busy",         Icon: Phone,        tone: "text-amber-700 bg-amber-50 border-amber-200" },
  { key: "wrong_number", label: "Wrong number", Icon: AlertCircle,  tone: "text-rose-700 bg-rose-50 border-rose-200" },
  { key: "other",        label: "Other",        Icon: MessageCircle,tone: "text-slate-700 bg-slate-50 border-slate-200" },
];

export default function QuickCallModal({ open, lead, onClose, onLogged }) {
  const [outcome, setOutcome] = useState("talked");
  const [duration, setDuration] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open || !lead) return null;

  const reset = () => {
    setOutcome("talked");
    setDuration("");
    setNotes("");
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { outcome };
      const mins = Number(duration);
      if (Number.isFinite(mins) && mins > 0) body.duration_minutes = Math.round(mins);
      if (notes.trim()) body.notes = notes.trim();
      const { data } = await api.post(`/leads/${lead.id}/call-logs`, body);
      toast.success("Call logged");
      window.dispatchEvent(new CustomEvent("af:followup-changed"));
      onLogged?.(data);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save call log");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={close}
      data-testid="quick-call-modal-backdrop"
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-[var(--line)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="quick-call-modal"
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b border-[var(--line)]">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 flex items-center gap-1.5">
              <PhoneCall size={11} /> Log this call
            </div>
            <div className="font-display font-bold text-base truncate" data-testid="quick-call-address">
              {lead.property_address}
            </div>
            {lead.seller_name && (
              <div className="text-xs text-[var(--ink-2)] truncate">
                {lead.seller_name}
                {lead.seller_phone ? ` · ${lead.seller_phone}` : ""}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            className="text-[var(--ink-3)] hover:text-[var(--ink)] shrink-0"
            aria-label="Close"
            data-testid="quick-call-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-[var(--ink-3)] mb-2">Outcome</div>
            <div className="flex flex-wrap gap-1.5">
              {OUTCOMES.map(({ key, label, Icon, tone }) => (
                <button
                  type="button"
                  key={key}
                  onClick={() => setOutcome(key)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border text-xs font-semibold transition ${
                    outcome === key
                      ? tone
                      : "border-[var(--line)] text-[var(--ink-2)] bg-white hover:bg-slate-50"
                  }`}
                  data-testid={`quick-call-outcome-${key}`}
                >
                  <Icon size={12} /> {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="field-label">Duration (min)</label>
              <input
                type="number"
                min="0"
                max="600"
                className="input"
                placeholder="4"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                data-testid="quick-call-duration"
              />
            </div>
            <div className="col-span-2">
              <label className="field-label">Notes</label>
              <input
                className="input"
                placeholder="What happened?"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={1000}
                data-testid="quick-call-notes"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-[var(--line)]">
          <button
            type="button"
            className="btn-ghost"
            onClick={close}
            disabled={busy}
            data-testid="quick-call-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-money"
            onClick={save}
            disabled={busy}
            data-testid="quick-call-save"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <PhoneCall size={14} />}
            {busy ? "Saving" : "Save call log"}
          </button>
        </div>
      </div>
    </div>
  );
}
